<template>
  <div
    class="h-full d-flex flex-column"
    :style="cssProps"
  >
    <v-card-text :class="{ 'px-0': isMobile, 'px-0': newsletterForm }">
      <div>
        <h3
          v-if="isUpdateForScheduledPost"
          class="post-reschedule-title"
        >
          <span v-if="newsletterForm">{{ $t('social-media.emails-reschedule') }}</span>
          <span v-else>{{ $t('social-media.post-reschedule') }}</span>
        </h3>
        <v-radio-group
          v-else
          v-model="isScheduledPost"
          class="isScheduledPost"
          hide-details
          :disabled="loading"
        >
          <v-radio
            class="mb-6"
            color="primary"
            :value="false"
            data-testid="content-creator-post-now-radio"
          >
            <template v-slot:label>
              <h3
                :class="{ 'radio-button-active': !isScheduledPost }"
                class="radio-button"
              >
                <span v-if="newsletterForm">{{ $t('social-media.post-now-newsletter') }}</span>
                <span v-else>{{ $t('social-media.post-now') }}</span>
              </h3>
            </template>
          </v-radio>
          <v-radio
            :value="true"
            color="primary"
            data-testid="content-creator-schedule-radio"
          >
            <template v-slot:label>
              <h3
                :class="{ 'radio-button-active': isScheduledPost }"
                class="radio-button"
              >
                <span v-if="newsletterForm">{{ $t('social-media.post-schedule-newsletter') }}</span>
                <span v-else>{{ $t('social-media.post-schedule') }}</span>
              </h3>
            </template>
          </v-radio>
        </v-radio-group>

        <v-expand-transition>
          <BlurredBackdropUpselling
            v-if="isScheduledPost"
            :is-blurred="!isSchedulePostEnabled"
          >
            <div class="gap-1 py-2 d-flex flex-column">
              <span
                v-if="!isUpdateForScheduledPost"
                class="schedule-text"
              >
                <span v-if="newsletterForm">
                  {{ $t('social-media.schedule.title-newsletter') }}
                </span>
                <span v-else>
                  {{ $t('social-media.schedule.title') }} <br /><br />
                  {{ $t('social-media.schedule.subtitle') }}
                </span>
              </span>
              <span
                v-else
                class="schedule-text"
              >
                <span v-if="newsletterForm">
                  {{ $t('social-media.reschedule.subtitle-newsletter') }}
                </span>
                <span v-else>
                  {{ $t('social-media.reschedule.subtitle') }}
                </span>
              </span>
              <DateTimeInput
                v-if="!hasCustomSchedules"
                :value="date"
                @input="updateDate"
              />
              <v-expand-transition v-else>
                <div class="gap-1 d-flex flex-column">
                  <div
                    v-for="page in pages"
                    :key="page.id"
                    class="flex-row gap-1 d-flex align-center"
                  >
                    <r-img
                      class="flex-grow-0"
                      :src="KindTypeIcon[TypeToKind[page.type]]"
                      width="36"
                      height="36"
                    />
                    <DateTimeInput
                      class="flex-grow-1"
                      :value="customSchedules[page.type].date"
                      @input="(value) => updateCustomSchedule(page.type, value)"
                    />
                  </div>
                </div>
              </v-expand-transition>
              <div v-if="pages.length > 1">
                <UpgradeInfoDialog :show-info="!isCustomSchedulePostEnabled">
                  <template v-slot="{ props }">
                    <!-- TODO: add plain style -->
                    <!-- variant="plain" -->
                    <v-btn
                      color="primary"
                      style="text-transform: none"
                      class="px-0"
                      variant="text"
                      dense
                      v-bind="props"
                      @click="toggleCustomSchedules"
                    >
                      {{
                        hasCustomSchedules
                          ? $t('social-media.schedule.common-schedule')
                          : $t('social-media.schedule.custom-schedule')
                      }}
                    </v-btn>
                  </template>
                </UpgradeInfoDialog>
              </div>
            </div>
          </BlurredBackdropUpselling>
        </v-expand-transition>
      </div>
    </v-card-text>

    <v-card-actions :class="{ 'px-0': newsletterForm }">
      <v-btn
        v-if="!isUpdateForScheduledPost"
        variant="text"
        class="back-btn"
        :disabled="loading"
        @click="$emit('back')"
      >
        {{ $t('labels.back') }}
      </v-btn>
      <v-btn
        v-else
        variant="text"
        :disabled="loading"
        class="close-btn"
        @click="$emit('close')"
      >
        {{ $t('labels.close') }}
      </v-btn>

      <v-spacer />

      <v-btn
        color="primary"
        variant="text"
        class="post-btn"
        :loading="loading || isPosting"
        :disabled="(isScheduledPost && !isSchedulePostEnabled) || isPosting"
        data-testid="content-creator-schedule-continue-btn"
        @click="handleOnPostClick"
      >
        <span v-if="newsletterForm">
          {{ $t('social-media.button.see-preview') }}
        </span>
        <span v-else-if="isUpdateForScheduledPost">
          {{
            isScheduledPost
              ? $t('buttons.social-media.post-schedule')
              : $t('buttons.social-media.post-now')
          }}
        </span>
        <span v-else>{{ $t('labels.continue') }}</span>
      </v-btn>
    </v-card-actions>
  </div>
</template>

<script>
import BlurredBackdropUpselling from '@/components/BlurredBackdropUpselling.vue'
import { useImageSentry } from '@/components/image-edit/composables/useImageSentry'
import UpgradeInfoDialog from '@/components/UpgradeInfoDialog.vue'
import ImageType from '@/lib/enums/ImageType'
import { cropImageToAspectRatio } from '@/lib/imageCropping'
import { showSnackbarMessage } from '@/lib/snackbarMessages'
import feature from '@/mixins/feature'
import EDIT_IMAGE from '@/modules/contentCreator/creator/queries/editImage.gql'
import PREPARE_STOCK_IMAGE_FOR_EDIT from '@/modules/contentCreator/creator/queries/prepareStockImageForEdit.gql'

import { KindTypeIcon, TypeToKind } from '../enums/KindType'
import ratioMap from '../lib/ratioMap.js'
import UPDATE_SAVED_CONTENT from '../queries/updateSavedContent.gql'
import UPDATE_SAVED_CONTENT_BATCH from '../queries/updateSavedContentBatch.gql'
import DateTimeInput from './DateTimeInput.vue'

// Wednesday at 11:00 is the time when the audience is most active
const BEST_TIME_TO_POST = {
  day: 3, // Wednesday
  hour: 11,
}

export default {
  components: {
    DateTimeInput,
    BlurredBackdropUpselling,
    UpgradeInfoDialog,
  },
  mixins: [feature],
  props: {
    pages: {
      type: Array,
      default: () => [],
    },
    content: {
      type: Object,
      default: () => {},
    },
    isUpdateForScheduledPost: {
      type: Boolean,
      default: false,
    },
    isScheduledPostPreselected: {
      type: Boolean,
      default: false,
    },
    newsletterForm: {
      type: Boolean,
      default: false,
    },
    isPosting: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      KindTypeIcon,
      TypeToKind,
      isScheduledPost: false,
      date: null,
      customSchedules: {},
      hasCustomSchedules: false,
      loading: false,
      sentry: null,
    }
  },
  computed: {
    cssProps() {
      return {
        '--primary': this.$vuetify.theme.global?.current?.colors.primary,
      }
    },
    isMasterView() {
      return this.$route.path.startsWith('/master')
    },
    isMobile() {
      return this.$vuetify.display.smAndDown
    },
    isSchedulePostEnabled() {
      return this.getFeature(this.featureNames.CONTENT_CREATOR).config.schedulePost
    },
    isCustomSchedulePostEnabled() {
      return this.getFeature(this.featureNames.CONTENT_CREATOR).config.customSchedulePost
    },
  },
  watch: {
    isScheduledPost(val) {
      this.$emit('isScheduledPost', val)
    },
    pages: {
      handler() {
        // Reset custom schedules when pages change
        this.hasCustomSchedules = false
        this.customSchedules = {}
      },
      deep: true,
    },
  },

  created() {
    this.isScheduledPost = this.isScheduledPostPreselected
    if (this.newsletterForm) {
      this.date = this.content.scheduledAt ? this.content.scheduledAt : new Date()
    } else {
      this.date = this.content.scheduledAt
        ? this.getScheduledPostDate()
        : this.getNextBestMomentToPost()
    }

    // Initialize Sentry helper
    this.sentry = useImageSentry({
      imageId: this.content?.id || 'unknown',
      imageType: ImageType.STOCK,
      topicId: this.content?.topicId,
    })
  },
  methods: {
    /**
     * Processes a stock image with proper cropping based on the content type
     * @param {Object} image - The image object to process
     * @returns {Promise<Object>} - The processed image object
     */
    async processStockImageWithCropping(image) {
      // Skip if not a stock image or if image is missing
      if (!image || image.provider !== 'STOCK') {
        return image
      }

      // Skip if image was already edited (has fileId or history)
      if (image.fileId || (image.history && image.history.length > 0)) {
        this.sentry.addBreadcrumb(
          'Skipping already edited stock image',
          {
            imageId: image.id,
            fileId: image.fileId,
            hasHistory: image.history?.length > 0,
          },
          'info'
        )
        return image
      }

      try {
        // First prepare the stock image for editing
        const { data: prepareData } = await this.$apollo.mutate({
          mutation: PREPARE_STOCK_IMAGE_FOR_EDIT,
          variables: {
            input: {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
              topicId: image.topicId,
            },
          },
        })

        // Skip if image is already licensed or can't be edited
        if (prepareData?.prepareStockImageForEdit?.isAlreadyLicensed) {
          this.sentry.addBreadcrumb(
            'Skipping already licensed stock image',
            {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
            },
            'info'
          )
          return image
        }

        if (!prepareData?.prepareStockImageForEdit?.canEdit) {
          this.sentry.captureImageWarning(
            'Stock image cannot be edited',
            {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
              prepareResponse: prepareData?.prepareStockImageForEdit,
            },
            'ScheduleStep'
          )
          return image
        }

        // Determine aspect ratio based on text kind
        const aspectRatio = this.getAspectRatioForImage()

        // Process the image using our utility function
        const processedImage = await cropImageToAspectRatio(
          prepareData.prepareStockImageForEdit.originalImageUrl,
          aspectRatio,
          this.sentry,
          'ScheduleStep'
        )

        if (!processedImage) {
          this.sentry.captureImageWarning(
            'Failed to process stock image',
            {
              imageId: image.id,
              provider: image.provider,
              externalId: image.externalId,
              originalImageUrl: prepareData.prepareStockImageForEdit.originalImageUrl,
              aspectRatio,
              textKind: this.content.textKind,
            },
            'ScheduleStep'
          )
          return image
        }

        // Edit the image with the processed version
        const { data: editData } = await this.$apollo.mutate({
          mutation: EDIT_IMAGE,
          variables: {
            input: {
              imageId: image.id,
              image: processedImage,
              imageType: ImageType.STOCK,
              topicId: image.topicId,
              provider: image.provider,
              externalId: image.externalId,
            },
          },
        })

        return editData?.editImage || image
      } catch (error) {
        this.sentry.captureImageError(
          error,
          'processStockImageWithCropping',
          {
            imageId: image.id,
            provider: image.provider,
            externalId: image.externalId,
            textKind: this.content.textKind,
          },
          'ScheduleStep'
        )
        // Return original image instead of throwing
        return image
      }
    },

    /**
     * Determines the appropriate aspect ratio based on content text kind
     * @returns {number} - The aspect ratio to use for cropping
     */
    getAspectRatioForImage() {
      // Get text kind from content
      const textKind = this.content.textKind

      // Use ratioMap to get the correct ratio for the text kind
      // Default to 1:1 ratio if no specific ratio is found
      return textKind && ratioMap[textKind] ? ratioMap[textKind] : 1
    },

    async processStockImage(image) {
      return this.processStockImageWithCropping(image)
    },

    async processImages() {
      if (!this.content.images?.length) return

      const processedImages = await Promise.all(
        this.content.images.map(async (image) => {
          try {
            return await this.processStockImage(image)
          } catch (error) {
            showSnackbarMessage('error', this.$t('alerts.image-edit.error-processing'))
            throw error
          }
        })
      )

      return processedImages
    },

    async processCarouselImages() {
      if (!this.content.carouselImages?.length) return

      const processedCarouselImages = await Promise.all(
        this.content.carouselImages.map(async (image) => {
          try {
            return await this.processStockImage(image)
          } catch (error) {
            showSnackbarMessage('error', this.$t('alerts.image-edit.error-processing'))
            throw error
          }
        })
      )

      return processedCarouselImages
    },

    async handleOnPostClick() {
      if (this.newsletterForm) {
        this.$emit('updateNewsletterForm', {
          scheduledAt: this.date,
          content: { ...this.content, textKind: 'newsletter' },
        })
        this.$tracking.event('Content Creator', 'Clicked', 'Share', this.content.textKind)
        return
      }

      // For updating scheduled posts, keep the existing update logic
      if (this.isUpdateForScheduledPost) {
        this.loading = true
        try {
          // Process images before updating
          const processedImages = await this.processImages()

          if (!this.hasCustomSchedules) {
            await this.updateSavedContent(processedImages)
            // Note: updateSavedContent will emit updatePostSuccess itself
            return
          } else {
            await this.updateSavedContentWithCustomSchedules(processedImages)
            // Note: updateSavedContentWithCustomSchedules will emit updatePostSuccess itself
            return
          }
        } catch (error) {
          showSnackbarMessage('error', this.$t('social-media.alert.something-went-wrong'))
        } finally {
          this.loading = false
        }
        return
      }

      // For new posts, emit schedule data instead of posting directly
      const scheduleData = {
        isScheduledPost: this.isScheduledPost,
        date: this.hasCustomSchedules ? undefined : this.date,
        customSchedules: this.hasCustomSchedules
          ? Object.entries(this.customSchedules).map(([type, { date }]) => ({
              type,
              scheduledAt: date,
            }))
          : undefined,
      }

      this.$tracking.event('Content Creator', 'Clicked', 'Share', this.content.textKind)
      this.$emit('scheduleSelected', scheduleData)
    },

    async updateSavedContent(processedImages) {
      const processedCarouselImages = await this.processCarouselImages()

      if (this.isMasterView && this.content.postingBatchId) {
        await this.$apollo.mutate({
          mutation: UPDATE_SAVED_CONTENT_BATCH,
          variables: {
            input: {
              postingBatchId: this.content.postingBatchId,
              scheduledAt: this.date,
            },
          },
        })
      } else {
        const input = {
          id: this.content.id,
          scheduledAt: this.date,
          ...(processedImages && { images: processedImages }),
          ...(processedCarouselImages && processedCarouselImages.length > 0
            ? { carouselImages: processedCarouselImages }
            : {}),
        }
        await this.$apollo.mutate({
          mutation: UPDATE_SAVED_CONTENT,
          variables: { input },
        })
      }

      const postsForUpdate = this.getPosts()
      this.$emit('updatePostSuccess', postsForUpdate)
      this.$tracking.event('Content Creator', 'Edited', 'Updated Saved Content')
    },

    async updateSavedContentWithCustomSchedules(processedImages) {
      const processedCarouselImages = await this.processCarouselImages()

      if (this.isMasterView && this.content.postingBatchId) {
        await this.$apollo.mutate({
          mutation: UPDATE_SAVED_CONTENT_BATCH,
          variables: {
            input: {
              postingBatchId: this.content.postingBatchId,
              customSchedules: Object.entries(this.customSchedules).map(([type, { date }]) => ({
                type,
                scheduledAt: date,
              })),
            },
          },
        })
      } else {
        const input = {
          id: this.content.id,
          customSchedules: Object.entries(this.customSchedules).map(([type, { date }]) => ({
            type,
            scheduledAt: date,
          })),
          ...(processedImages && { images: processedImages }),
          ...(processedCarouselImages && processedCarouselImages.length > 0
            ? { carouselImages: processedCarouselImages }
            : {}),
        }
        await this.$apollo.mutate({
          mutation: UPDATE_SAVED_CONTENT,
          variables: { input },
        })
      }

      this.$emit('updatePostSuccess', this.getPosts())
      this.$tracking.event('Content Creator', 'Edited', 'Updated Custom Schedules')
    },

    getPosts() {
      let result
      if (!this.isScheduledPost) {
        result = [{ platforms: this.pages.map((page) => page.type) }]
      } else if (!this.hasCustomSchedules) {
        result = [{ scheduledAt: this.date, platforms: this.pages.map((page) => page.type) }]
      } else {
        result = this.groupByDateTime()
      }
      return result
    },
    groupByDateTime() {
      const groupedByDate = Object.entries(this.customSchedules).reduce((acc, [type, { date }]) => {
        if (!acc[date]) {
          acc[date] = []
        }
        acc[date].push(type)
        return acc
      }, {})

      return Object.entries(groupedByDate).map(([scheduledAt, platforms]) => ({
        scheduledAt,
        platforms,
      }))
    },
    toggleCustomSchedules() {
      if (!this.isCustomSchedulePostEnabled) {
        return
      }
      if (!this.hasCustomSchedules) {
        this.pages.forEach((page) => {
          this.customSchedules[page.type] = { date: this.date }
        })
        this.hasCustomSchedules = true
      } else {
        this.hasCustomSchedules = false
        this.customSchedules = {}
      }
    },

    getScheduledPostDate() {
      return new Date(this.content.socialMediaPosts[0].scheduledAt).toISOString()
    },

    getNextBestMomentToPost() {
      const now = new Date()
      const dayOfWeek = now.getDay()
      const daysUntilNextWednesday = (BEST_TIME_TO_POST.day - dayOfWeek + 7) % 7 || 7
      const nextWednesday = new Date(now)
      nextWednesday.setDate(now.getDate() + daysUntilNextWednesday)
      nextWednesday.setHours(BEST_TIME_TO_POST.hour, 0, 0, 0)
      return nextWednesday.toISOString()
    },
    updateDate(value) {
      this.date = value
    },

    updateCustomSchedule(type, value) {
      if (!this.customSchedules[type]) {
        this.customSchedules[type] = { date: value }
      } else {
        this.customSchedules[type]['date'] = value
      }
    },
  },
  emits: [
    'isScheduledPost',
    'updatePostSuccess',
    'savedScheduledContent',
    'success',
    'back',
    'close',
    'updateNewsletterForm',
    'scheduleSelected',
  ],
}
</script>

<style>
.isScheduledPost .v-radio {
  display: flex;
  flex-direction: row-reverse;
  justify-content: space-between;
}

.post-reschedule-title,
.radio-button {
  font-size: 16px !important;
  line-height: 22px;
  font-style: normal;
  color: var(--primary);
}

.post-reschedule-title {
  font-weight: 600;
}

.radio-button {
  font-weight: 400;
}

.radio-button-active {
  font-weight: 800;
}

.post-btn,
.close-btn,
.back-btn {
  font-size: 14px !important;
  font-style: normal;
  font-weight: 600;
  line-height: 16px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
}

.schedule-text {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
}
</style>
